import React, { useState, useEffect } from "react";
import { Facebook, ExternalLink, ShieldCheck } from "lucide-react";

const PAGE_URL = "https://www.facebook.com/spireinclusion";
const CONSENT_KEY = "spire-cookie-consent";

const pluginSrc = (height) => {
  const params = new URLSearchParams({
    href: PAGE_URL,
    tabs: "timeline",
    width: "500",
    height: String(height),
    small_header: "true",
    adapt_container_width: "true",
    hide_cover: "true",
    show_facepile: "false",
  });
  return `https://www.facebook.com/plugins/page.php?${params.toString()}`;
};

export const FacebookFeed = ({
  title = "Follow us on Facebook",
  subtitle = "The latest straight from our page.",
  height = 640,
}) => {
  const [allowed, setAllowed] = useState(false);

  useEffect(() => {
    setAllowed(localStorage.getItem(CONSENT_KEY) === "accepted");
    const onConsent = (e) => setAllowed(e.detail === "accepted");
    window.addEventListener("spire-consent", onConsent);
    return () => window.removeEventListener("spire-consent", onConsent);
  }, []);

  const enableFeed = () => {
    localStorage.setItem(CONSENT_KEY, "accepted");
    window.dispatchEvent(new CustomEvent("spire-consent", { detail: "accepted" }));
    setAllowed(true);
  };

  return (
    <section
      aria-label="Our Facebook feed"
      className="max-w-2xl mx-auto"
      data-testid="facebook-feed"
    >
      <div
        className="overflow-hidden rounded-3xl border border-orange-200 bg-gradient-to-b from-orange-50 to-white shadow-sm"
        data-testid="facebook-feed-card"
      >
        {/* Branded header */}
        <div className="flex items-center gap-4 border-b border-orange-100 bg-white/70 px-6 py-5 sm:px-8">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-orange-600 text-white shadow-sm">
            <Facebook className="h-6 w-6" aria-hidden="true" />
          </span>
          <div className="flex-1">
            <h2 className="font-heading text-2xl font-semibold text-stone-900">
              {title}
            </h2>
            <p className="text-base text-stone-600">{subtitle}</p>
          </div>
          <a
            href={PAGE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-orange-600 px-5 py-2.5 font-semibold text-white transition-colors hover:bg-orange-700"
            data-testid="facebook-feed-follow"
          >
            Give us a follow
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
          </a>
        </div>

        {/* Feed body */}
        <div className="p-4 sm:p-6">
          {allowed ? (
            <div
              className="mx-auto max-w-[500px] overflow-hidden rounded-2xl bg-white shadow-inner ring-1 ring-orange-100"
              data-testid="facebook-feed-embed"
            >
              <iframe
                title="Spire Inclusion Facebook feed"
                src={pluginSrc(height)}
                className="w-full"
                style={{ height: `${height}px`, border: "none", overflow: "hidden" }}
                scrolling="no"
                frameBorder="0"
                allow="encrypted-media"
                allowFullScreen
              />
            </div>
          ) : (
            <div className="px-2 py-6 text-center" data-testid="facebook-feed-consent">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-orange-700 shadow-sm ring-1 ring-orange-100">
                <ShieldCheck className="h-7 w-7" aria-hidden="true" />
              </span>
              <p className="mx-auto mt-4 max-w-md text-lg text-stone-800 leading-relaxed">
                Our live Facebook feed uses cookies from Facebook to load. Choose{" "}
                <span className="font-semibold">Show Facebook feed</span> to load it, or visit our
                page directly.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  type="button"
                  onClick={enableFeed}
                  className="px-6 py-3 rounded-xl bg-orange-600 text-white font-semibold text-lg transition-colors hover:bg-orange-700"
                  data-testid="facebook-feed-enable"
                >
                  Show Facebook feed
                </button>
                <a
                  href={PAGE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-white text-stone-900 border-2 border-stone-300 font-semibold text-lg transition-colors hover:border-orange-600 hover:bg-orange-50"
                  data-testid="facebook-feed-visit"
                >
                  Visit our page
                  <ExternalLink className="h-5 w-5" aria-hidden="true" />
                </a>
              </div>
            </div>
          )}
        </div>

        {/* Mobile follow button */}
        <div className="border-t border-orange-100 bg-white/70 px-6 py-4 sm:hidden">
          <a
            href={PAGE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex w-full items-center justify-center gap-2 rounded-full bg-orange-600 px-5 py-3 font-semibold text-white transition-colors hover:bg-orange-700"
            data-testid="facebook-feed-follow-mobile"
          >
            Give us a follow
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
          </a>
        </div>
      </div>
    </section>
  );
};
